import React, { useCallback, useEffect, useState } from "react";
import useToastAlert from "@/hooks/useToastAlert";
import { Severity } from "@velaro/velaro-component-lib";
import {
  SectionCard,
  PrimaryButton,
  FieldInput,
  Divider,
  IconButton
} from "@/components/ds";
import { Lightbulb, Trash } from "@/components/ds/icons";
import IconByName from "@/components/ds/IconByName";
import { Link, useNavigate } from "react-router-dom";
import useApi from "@/hooks/useApi";
import LoadingSpinner from "@/components/LoadingSpinner";
import DynamicsAccounts from "./DynamicsAccounts";
import IntegrationActivityLog from "../IntegrationActivityLog";
import StarterWorkflowWizard from "../StarterWorkflowWizard";
import LaunchWizardButton from "@/components/LaunchWizardButton";
import { IconClose1 } from "@velaro/velaro-component-lib";
import SkillControlsPanel from "../SkillControlsPanel";
import StickyRouteHeader from "@/pages/Shared/StickyRouteHeader";
import { IntegrationTab } from "../shared/IntegrationTab";

interface DynamicsConfig {
  id: number;
  siteId: number;
  displayName: string;
  tenantId: string;
  clientId: string;
  instanceUrl: string;
  orgName: string;
  enabled: boolean;
  createdAt: string;
  clientSecretMasked: string;
  clientSecretExpiresAt?: string | null;
  secretStatus?: "ok" | "expiring_soon" | "expired" | null;
  pushTranscripts: boolean;
  customFieldMappingsJson?: string | null;
  attachAsActivity: boolean;
  preferAccount: boolean;
  activityLogicalName?: string | null;
}

interface DynamicsFieldMapping {
  velaroAttribute: string;
  dynamicsField: string;
  label: string;
}

const CAPABILITIES = [
  {
    title: "Contact & account lookup",
    icon: "User",
    items: [
      "Search Dynamics 365 contacts by email or full name — returns job title, phone, and linked account",
      "Search accounts (companies) by name — returns website, phone number, annual revenue, and employee count",
      "Retrieve the complete contact record: address, linked opportunities, open cases, and full activity history",
      "Supports multiple Dynamics 365 environments — connect different organizations or sandboxes per site"
    ]
  },
  {
    title: "Record creation & notes",
    icon: "FileText",
    items: [
      "Create new Lead records from visitor data collected during the chat — first name, last name, email, phone, company, topic",
      "Push notes (e.g. conversation summaries or full transcripts) to any Dynamics 365 entity: Contact, Account, Lead, Opportunity, or Case",
      "Notes are attached via the standard Annotations table and appear in the entity's timeline in Dynamics 365"
    ]
  },
  {
    title: "Rules engine automation",
    icon: "Settings",
    items: [
      "Use the Rules Engine to automatically create Dynamics 365 records when conversations match conditions — no agent action required",
      "Supported record types: Contact, Account, Lead, Opportunity, Case, Note",
      "Example: when a visitor from a known company asks about pricing → auto-create a Lead and push note",
      "Rules run server-side on chat events (start, end, transfer, survey complete)"
    ]
  },
  {
    title: "Microsoft & AppSource integration",
    icon: "Building",
    items: [
      "Uses OAuth 2.0 client credentials (Entra App Registration) — no user login required after setup",
      "Works with any Dynamics 365 Sales, Service, or Customer Engagement environment on v9.2+",
      "Velaro's native Entra ID login + D365 integration qualifies for Microsoft AppSource listing",
      "Customers on Microsoft 365 / Azure tenants can deploy Velaro directly from AppSource"
    ]
  },
  {
    title: "AI workflow tools (28)",
    icon: "Bot",
    items: [
      "28 AI tools available in every workflow and bot node: contacts (4), accounts + product catalog (4), leads (3), opportunities + quotes (6), cases (5), activities, notes & scheduling (6)",
      "Create Dynamics 365 quotes linked to opportunities and send the quote ID directly in chat — close B2B deals without leaving the conversation",
      "Schedule Dynamics 365 appointments that sync to Outlook Calendar — book follow-up meetings mid-chat",
      "Pull full email history with a contact so the AI can reference prior conversations during the chat",
      "Search the Dynamics product catalog for pricing and availability — useful for B2B sales bots quoting line items"
    ]
  }
];

const AI_TOOLS = [
  // Contact (4)
  {
    name: "dynamics365_search_contacts",
    label: "Search contacts",
    group: "Contact",
    description:
      "Find Dynamics 365 contacts by email or name. Returns full name, job title, phone, and linked account."
  },
  {
    name: "dynamics365_get_contact",
    label: "Get contact details",
    group: "Contact",
    description:
      "Fetch the full contact record including address, linked opportunities, open cases, and activity history."
  },
  {
    name: "dynamics365_get_contact_by_email",
    label: "Get contact by email",
    group: "Contact",
    description:
      "Direct email lookup — retrieves a contact record by exact email address."
  },
  {
    name: "dynamics365_update_contact",
    label: "Update contact",
    group: "Contact",
    description:
      "Update specific fields on an existing contact — phone, email, job title, or description."
  },
  // Account (3)
  {
    name: "dynamics365_search_accounts",
    label: "Search accounts",
    group: "Account",
    description:
      "Find Dynamics 365 accounts (companies) by name. Returns website, phone, revenue, and employee count."
  },
  {
    name: "dynamics365_get_account",
    label: "Get account details",
    group: "Account",
    description:
      "Retrieve full account record including revenue, industry, address, and relationship manager."
  },
  {
    name: "dynamics365_get_account_contacts",
    label: "Get account contacts",
    group: "Account",
    description:
      "List all contacts associated with an account — useful for B2B conversations."
  },
  // Lead (3)
  {
    name: "dynamics365_create_lead",
    label: "Create lead",
    group: "Lead",
    description:
      "Create a new Lead record in Dynamics 365 from visitor data collected during the conversation."
  },
  {
    name: "dynamics365_search_leads",
    label: "Search leads",
    group: "Lead",
    description:
      "Find leads by name, email, company, or source. Returns status and qualification stage."
  },
  {
    name: "dynamics365_update_lead",
    label: "Update lead",
    group: "Lead",
    description:
      "Update a lead's qualification status, source, or contact details."
  },
  // Opportunity (5)
  {
    name: "dynamics365_search_opportunities",
    label: "Search opportunities",
    group: "Opportunity",
    description:
      "Find open opportunities for a contact or account — shows estimated value, stage, and close date."
  },
  {
    name: "dynamics365_get_opportunity",
    label: "Get opportunity details",
    group: "Opportunity",
    description:
      "Retrieve full opportunity record: estimated revenue, pipeline stage, products, and close date."
  },
  {
    name: "dynamics365_get_contact_opportunities",
    label: "Get contact's opportunities",
    group: "Opportunity",
    description: "List all opportunities linked to a specific contact."
  },
  {
    name: "dynamics365_update_opportunity",
    label: "Update opportunity",
    group: "Opportunity",
    description:
      "Update an opportunity's stage, estimated value, close date, or probability."
  },
  {
    name: "dynamics365_create_opportunity",
    label: "Create opportunity",
    group: "Opportunity",
    description:
      "Create a new Dynamics 365 opportunity linked to a contact from conversation data."
  },
  // Case (5)
  {
    name: "dynamics365_search_cases",
    label: "Search cases",
    group: "Case",
    description:
      "Search open support cases by title or linked contact. Shows status, priority, and case number."
  },
  {
    name: "dynamics365_get_case",
    label: "Get case details",
    group: "Case",
    description:
      "Retrieve full case record — subject, description, priority, status, and linked contact."
  },
  {
    name: "dynamics365_create_case",
    label: "Create case",
    group: "Case",
    description:
      "Create a new support Case with subject, description, and priority from conversation data."
  },
  {
    name: "dynamics365_update_case",
    label: "Update case",
    group: "Case",
    description:
      "Update case status, priority, resolution notes, or assigned owner."
  },
  {
    name: "dynamics365_get_contact_cases",
    label: "Get contact's cases",
    group: "Case",
    description:
      "List all support cases linked to a contact — useful for repeat-issue detection."
  },
  // Activity & Notes (4)
  {
    name: "dynamics365_create_note",
    label: "Create note",
    group: "Activity & Notes",
    description:
      "Attach a note (conversation summary or transcript) to any Dynamics entity — contact, account, lead, or case."
  },
  {
    name: "dynamics365_get_activities",
    label: "Get activities",
    group: "Activity & Notes",
    description:
      "Retrieve recent activities for a contact — calls, emails, tasks, and meetings."
  },
  {
    name: "dynamics365_create_activity",
    label: "Create activity",
    group: "Activity & Notes",
    description:
      "Log a call, task, or meeting activity against a Dynamics entity from the conversation."
  },
  {
    name: "dynamics365_get_entity_notes",
    label: "Get entity notes",
    group: "Activity & Notes",
    description:
      "Retrieve all notes attached to a Dynamics entity — useful for reviewing prior interactions."
  },
  {
    name: "dynamics365_create_quote",
    label: "Create quote",
    group: "Opportunity",
    description:
      "Create a Dynamics 365 quote optionally linked to an opportunity. Returns the new quote ID — lets the AI generate formal quotes for B2B customers directly in chat."
  },
  {
    name: "dynamics365_schedule_appointment",
    label: "Schedule appointment",
    group: "Activity & Notes",
    description:
      "Create a Dynamics 365 appointment for a contact. Syncs to Outlook calendar and sends invites — lets the AI book follow-up meetings without leaving the chat."
  },
  {
    name: "dynamics365_get_email_history",
    label: "Get email history",
    group: "Activity & Notes",
    description:
      "Get email communication history with a Dynamics contact. Shows sent and received emails with dates and subjects — helps the AI reference prior conversations."
  },
  {
    name: "dynamics365_get_products",
    label: "Search product catalog",
    group: "Account",
    description:
      "Search the Dynamics 365 product catalog by name or product number. Returns pricing and availability — useful for B2B sales bots quoting line items from the conversation."
  },
  // Case & Opportunity closing, RMA, Field Service — added 2026-09-07 (QA: these 5 skills were
  // callable but missing from this admin UI list, so they could not be enabled/described/reviewed)
  {
    name: "dynamics365_close_case",
    label: "Close case",
    group: "Case",
    description:
      "Resolve and close a Dynamics 365 support case via the CloseIncident action, with a resolution summary."
  },
  {
    name: "dynamics365_close_opportunity",
    label: "Close opportunity",
    group: "Opportunity",
    description:
      "Close a Dynamics 365 opportunity as Won or Lost via the Win/Lose Opportunity action."
  },
  {
    name: "dynamics365_get_rma_status",
    label: "Get RMA status",
    group: "Field Service",
    description:
      "Look up a Dynamics 365 Field Service RMA (return merchandise authorization) by ID or RMA number — process status, amount, and linked account."
  },
  {
    name: "dynamics365_create_rma",
    label: "Create RMA",
    group: "Field Service",
    description:
      "Create a new Dynamics 365 Field Service RMA (return) for a customer, optionally linked to a contact by email."
  },
  {
    name: "dynamics365_get_field_service_order",
    label: "Get field service order",
    group: "Field Service",
    description:
      "Look up a Dynamics 365 Field Service work order by ID or work order number — status, priority, address, and service window."
  }
];

const AI_TOOL_GROUPS = [
  "Contact",
  "Account",
  "Lead",
  "Opportunity",
  "Case",
  "Activity & Notes"
];

function CapabilitiesPanel() {
  const [open, setOpen] = useState(false);
  const [openSections, setOpenSections] = useState<Set<number>>(new Set([0]));

  function toggleSection(i: number) {
    setOpenSections((prev) => {
      const next = new Set(prev);
      if (next.has(i)) next.delete(i);
      else next.add(i);
      return next;
    });
  }

  return (
    <SectionCard className="mb-4">
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between px-6 py-4 text-left hover:bg-zinc-50 rounded-2xl"
      >
        <div className="flex items-center gap-3">
          <span className="font-semibold text-zinc-800">What's included</span>
          <span className="text-xs bg-blue-100 text-blue-700 font-medium px-2 py-0.5 rounded-full">
            {CAPABILITIES.length} features
          </span>
        </div>
        <svg
          className={`w-4 h-4 text-zinc-400 transition-transform shrink-0 ${open ? "rotate-180" : ""}`}
          viewBox="0 0 16 16"
          fill="currentColor"
        >
          <path d="M8 10.5L3 5.5H13L8 10.5Z" />
        </svg>
      </button>

      {open && (
        <>
          <Divider />
          <div className="px-6 pb-6">
            <div className="space-y-2 mt-4">
              {CAPABILITIES.map((cap, i) => (
                <div
                  key={i}
                  className="rounded-lg border border-zinc-100 overflow-hidden"
                >
                  <button
                    type="button"
                    onClick={() => toggleSection(i)}
                    className="w-full flex items-center justify-between px-4 py-3 text-left hover:bg-zinc-50"
                  >
                    <div className="flex items-center gap-3">
                      <span className="text-zinc-600">
                        <IconByName name={cap.icon} className="w-4 h-4" />
                      </span>
                      <span className="text-sm font-medium text-zinc-700">
                        {cap.title}
                      </span>
                    </div>
                    <svg
                      className={`w-3.5 h-3.5 text-zinc-400 transition-transform shrink-0 ${openSections.has(i) ? "rotate-180" : ""}`}
                      viewBox="0 0 16 16"
                      fill="currentColor"
                    >
                      <path d="M8 10.5L3 5.5H13L8 10.5Z" />
                    </svg>
                  </button>
                  {openSections.has(i) && (
                    <div className="px-4 pb-3 space-y-1.5 border-t border-zinc-50">
                      {cap.items.map((item, j) => (
                        <div key={j} className="flex items-start gap-2 pt-1.5">
                          <div className="mt-1.5 w-1.5 h-1.5 rounded-full bg-blue-500 shrink-0" />
                          <span className="text-xs text-zinc-600">{item}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </>
      )}
    </SectionCard>
  );
}

// ─── Entity config ─────────────────────────────────────────────────────────────

type DynamicsEntity =
  "contact" | "account" | "lead" | "opportunity" | "incident";

const ENTITIES: {
  key: DynamicsEntity;
  label: string;
  displayName: string;
  icon: string;
  sidebarNote: string;
}[] = [
  {
    key: "contact",
    label: "Contact",
    displayName: "Contact",
    icon: "User",
    sidebarNote:
      "Shown in the Dynamics panel → Contact tab when an agent has a live conversation with a matched contact."
  },
  {
    key: "account",
    label: "Account",
    displayName: "Account / Company",
    icon: "Building",
    sidebarNote:
      "Shown in the Dynamics panel → Contact tab under the 'Account' section when the contact has a linked account."
  },
  {
    key: "lead",
    label: "Lead",
    displayName: "Lead",
    icon: "Tag",
    sidebarNote:
      "Shown in the Dynamics panel when the visitor matches an open Lead record."
  },
  {
    key: "opportunity",
    label: "Opportunity",
    displayName: "Opportunity",
    icon: "Briefcase",
    sidebarNote:
      "Shown in the Dynamics panel → Opportunities tab alongside deal value and close date."
  },
  {
    key: "incident",
    label: "Case",
    displayName: "Case (Incident)",
    icon: "FileText",
    sidebarNote:
      "Shown when the contact has open support cases linked in Dynamics."
  }
];

const STANDARD_FIELDS: Record<
  DynamicsEntity,
  { fieldName: string; label: string; type: string }[]
> = {
  contact: [
    { fieldName: "fullname", label: "Full Name", type: "String" },
    { fieldName: "emailaddress1", label: "Email Address", type: "String" },
    { fieldName: "jobtitle", label: "Job Title", type: "String" },
    {
      fieldName: "_parentcustomerid_value",
      label: "Account / Company",
      type: "Lookup"
    },
    { fieldName: "telephone1", label: "Business Phone", type: "String" },
    { fieldName: "mobilephone", label: "Mobile Phone", type: "String" },
    { fieldName: "address1_city", label: "City", type: "String" },
    { fieldName: "address1_country", label: "Country", type: "String" }
  ],
  account: [
    { fieldName: "name", label: "Account Name", type: "String" },
    { fieldName: "websiteurl", label: "Website", type: "String" },
    { fieldName: "telephone1", label: "Phone", type: "String" },
    { fieldName: "revenue", label: "Annual Revenue", type: "Money" },
    { fieldName: "numberofemployees", label: "Employees", type: "Integer" },
    { fieldName: "industrycode", label: "Industry", type: "Picklist" }
  ],
  lead: [
    { fieldName: "fullname", label: "Full Name", type: "String" },
    { fieldName: "emailaddress1", label: "Email", type: "String" },
    { fieldName: "companyname", label: "Company", type: "String" },
    { fieldName: "jobtitle", label: "Job Title", type: "String" },
    { fieldName: "leadsourcecode", label: "Lead Source", type: "Picklist" },
    { fieldName: "statuscode", label: "Status", type: "Status" }
  ],
  opportunity: [
    { fieldName: "name", label: "Opportunity Name", type: "String" },
    { fieldName: "estimatedvalue", label: "Est. Value", type: "Money" },
    { fieldName: "estimatedclosedate", label: "Close Date", type: "DateTime" },
    { fieldName: "salesstage", label: "Sales Stage", type: "Picklist" },
    { fieldName: "statuscode", label: "Status", type: "Status" }
  ],
  incident: [
    { fieldName: "title", label: "Case Title", type: "String" },
    { fieldName: "ticketnumber", label: "Case Number", type: "String" },
    { fieldName: "prioritycode", label: "Priority", type: "Picklist" },
    { fieldName: "statuscode", label: "Status", type: "Status" },
    { fieldName: "createdon", label: "Created", type: "DateTime" }
  ]
};

interface SidebarField {
  fieldName: string;
  label: string;
  entity: DynamicsEntity;
  attributeType?: string;
}

interface DiscoveredProperty {
  name: string;
  label: string;
  attributeType: string;
}

function TypeBadge({ type }: { type: string }) {
  const color =
    type === "String"
      ? "bg-blue-50 text-blue-600 border-blue-100"
      : type === "Integer" ||
          type === "BigInt" ||
          type === "Decimal" ||
          type === "Double"
        ? "bg-purple-50 text-purple-600 border-purple-100"
        : type === "Money"
          ? "bg-green-50 text-green-600 border-green-100"
          : type === "Boolean"
            ? "bg-orange-50 text-orange-600 border-orange-100"
            : type === "DateTime"
              ? "bg-amber-50 text-amber-700 border-amber-100"
              : type === "Picklist" || type === "Status" || type === "State"
                ? "bg-rose-50 text-rose-600 border-rose-100"
                : type === "Memo"
                  ? "bg-zinc-100 text-zinc-500 border-zinc-200"
                  : "bg-zinc-100 text-zinc-500 border-zinc-200";
  return (
    <span
      className={`text-[9px] font-semibold px-1.5 py-0.5 rounded border ${color} shrink-0`}
    >
      {type}
    </span>
  );
}

function VariableChip({
  entity,
  fieldName
}: {
  entity: DynamicsEntity;
  fieldName: string;
}) {
  const [copied, setCopied] = useState(false);
  const variable = `{dynamics.${entity}.${fieldName}}`;
  async function copy() {
    try {
      await navigator.clipboard.writeText(variable);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      /* */
    }
  }
  return (
    <button
      type="button"
      onClick={copy}
      title={`Copy: ${variable}`}
      aria-label={`Copy variable ${variable}`}
      className={`text-[9px] px-1.5 py-0.5 rounded font-mono border transition-colors ${
        copied
          ? "border-green-300 bg-green-50 text-green-700"
          : "border-zinc-200 bg-zinc-50 text-zinc-400 hover:border-blue-300 hover:text-blue-600 hover:bg-blue-50"
      }`}
    >
      {copied ? "✓ copied" : variable}
    </button>
  );
}

function DynamicsSidebarFieldsTab() {
  const api = useApi();
  const [activeEntity, setActiveEntity] = useState<DynamicsEntity>("contact");
  const [customFields, setCustomFields] = useState<SidebarField[]>([]);
  // Cache of discovered properties per entity
  const [propertiesCache, setPropertiesCache] = useState<
    Partial<Record<DynamicsEntity, DiscoveredProperty[]>>
  >({});
  const [loadingProperties, setLoadingProperties] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [selectedProp, setSelectedProp] = useState("");
  const [customLabel, setCustomLabel] = useState("");
  const [filter, setFilter] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const resp = await api.messaging.get("Dynamics/data/sidebar-fields");
        if (resp.ok) {
          const data = await resp.json();
          setCustomFields(Array.isArray(data?.fields) ? data.fields : []);
        }
      } catch {
        /* safe default */
      }
    })();
  }, [api.messaging]);

  const entityConfig = ENTITIES.find((e) => e.key === activeEntity)!;
  const entityProperties = propertiesCache[activeEntity] ?? [];
  const entityPropertiesLoaded = activeEntity in propertiesCache;
  const entityCustomFields = customFields.filter(
    (f) => (f.entity ?? "contact") === activeEntity
  );

  const filteredProperties = filter
    ? entityProperties.filter(
        (p) =>
          p.name.includes(filter.toLowerCase()) ||
          p.label.toLowerCase().includes(filter.toLowerCase())
      )
    : entityProperties;

  async function loadProperties(entity: DynamicsEntity) {
    if (entity in propertiesCache) return;
    setLoadingProperties(true);
    try {
      const resp = await api.messaging.get(
        `Dynamics/data/properties?entity=${entity}`
      );
      if (resp.ok) {
        const data = await resp.json();
        setPropertiesCache((prev) => ({
          ...prev,
          [entity]: Array.isArray(data?.properties) ? data.properties : []
        }));
      }
    } catch {
      /* non-fatal */
    } finally {
      setLoadingProperties(false);
    }
  }

  function addField() {
    if (!selectedProp) return;
    const prop = entityProperties.find((p) => p.name === selectedProp);
    if (!prop) return;
    const label = customLabel.trim() || prop.label;
    if (
      customFields.some(
        (f) =>
          f.fieldName === selectedProp &&
          (f.entity ?? "contact") === activeEntity
      )
    )
      return;
    setCustomFields((prev) => [
      ...prev,
      {
        fieldName: selectedProp,
        label,
        entity: activeEntity,
        attributeType: prop.attributeType
      }
    ]);
    setSelectedProp("");
    setCustomLabel("");
    setFilter("");
  }

  function removeField(fieldName: string, entity: DynamicsEntity) {
    setCustomFields((prev) =>
      prev.filter(
        (f) =>
          !(f.fieldName === fieldName && (f.entity ?? "contact") === entity)
      )
    );
  }

  async function save() {
    setSaving(true);
    try {
      await api.messaging.post("Dynamics/data/sidebar-fields", {
        fields: customFields
      });
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch {
      /* non-fatal */
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="space-y-4">
      {/* Where it shows — callout */}
      <SectionCard
        title="Sidebar Fields — Dynamics 365"
        description="Configure which Dynamics fields appear in the agent conversation panel. Standard fields are always shown. Add custom or org-specific fields from any entity — Contact, Account, Lead, Opportunity, or Case."
      >
        {/* Visual "where it shows" panel */}
        <div className="rounded-xl border border-blue-200 bg-blue-50 p-4 mb-2">
          <div className="text-xs font-semibold text-blue-800 mb-2">
            Where these fields appear
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {/* Mockup of the agent sidebar */}
            <div className="bg-white rounded-lg border border-blue-100 p-3 shadow-sm">
              <div className="flex items-center gap-1.5 mb-2">
                <div className="w-3 h-3 rounded-full bg-blue-500 shrink-0" />
                <span className="text-[10px] font-semibold text-zinc-700">
                  Dynamics 365 panel (agent sidebar)
                </span>
              </div>
              <div className="border-b border-zinc-100 mb-2 pb-1 flex gap-2 text-[9px] font-medium">
                <span className="border-b-2 border-blue-500 text-blue-700 pb-0.5">
                  Contact
                </span>
                <span className="text-zinc-400">Opportunities</span>
              </div>
              <div className="space-y-1">
                {[
                  "Sarah Johnson",
                  "VP of Engineering",
                  "Acme Corp • sarah@acme.com"
                ].map((v) => (
                  <div key={v} className="text-[9px] text-zinc-600">
                    {v}
                  </div>
                ))}
                <div className="mt-1.5">
                  <Divider />
                  <div className="pt-1.5">
                    <div className="text-[8px] uppercase text-zinc-400 font-semibold tracking-wide mb-1">
                      Custom fields
                    </div>
                    <div className="flex justify-between text-[9px]">
                      <span className="text-zinc-500">Contract Value</span>
                      <span className="font-medium text-zinc-700">$48,000</span>
                    </div>
                    <div className="flex justify-between text-[9px]">
                      <span className="text-zinc-500">Territory</span>
                      <span className="font-medium text-zinc-700">
                        Northeast
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className="space-y-2 text-xs text-blue-800">
              <p>
                <strong>Contact fields</strong> → appear in the Contact tab when
                a visitor's email matches a Dynamics contact.
              </p>
              <p>
                <strong>Account fields</strong> → appear below the contact
                details when the contact has a linked account.
              </p>
              <p>
                <strong>Opportunity fields</strong> → appear in the
                Opportunities tab alongside deal value and stage.
              </p>
              <p>
                <strong>Variable syntax</strong> for HTTP POST nodes:{" "}
                <code className="bg-blue-100 px-1 rounded text-[10px]">
                  &#123;dynamics.contact.fieldname&#125;
                </code>
              </p>
            </div>
          </div>
        </div>
      </SectionCard>

      {/* Entity tabs + field picker */}
      <SectionCard>
        {/* Entity tab bar */}
        <div className="flex border-b border-zinc-200 px-6 pt-4 gap-1 overflow-x-auto">
          {ENTITIES.map((e) => {
            const count = customFields.filter(
              (f) => (f.entity ?? "contact") === e.key
            ).length;
            return (
              <button
                key={e.key}
                type="button"
                onClick={() => {
                  setActiveEntity(e.key);
                  setSelectedProp("");
                  setCustomLabel("");
                  setFilter("");
                }}
                className={`flex items-center gap-1.5 px-3 py-2 text-sm whitespace-nowrap rounded-t-lg border-b-2 transition-colors ${
                  activeEntity === e.key
                    ? "border-blue-500 text-blue-700 font-medium bg-blue-50"
                    : "border-transparent text-zinc-500 hover:text-zinc-700"
                }`}
              >
                <span className="text-zinc-600">
                  <IconByName name={e.icon} className="w-4 h-4" />
                </span>
                <span>{e.label}</span>
                {count > 0 && (
                  <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-blue-100 text-blue-700 font-semibold">
                    {count}
                  </span>
                )}
              </button>
            );
          })}
        </div>

        <div className="p-6">
          {/* Entity sidebar note */}
          <div className="text-xs text-zinc-500 mb-4 border-l-2 border-zinc-300 px-3 py-2">
            <strong className="text-zinc-700 inline-flex items-center gap-1.5">
              <IconByName name={entityConfig.icon} className="w-4 h-4" />{" "}
              {entityConfig.displayName}:
            </strong>{" "}
            {entityConfig.sidebarNote}
          </div>

          {/* Standard fields for this entity */}
          <div className="mb-5">
            <div className="flex items-center gap-2 mb-2">
              <div className="text-xs font-semibold text-zinc-500 uppercase tracking-wide">
                Standard fields
              </div>
              <span className="text-[10px] px-1.5 py-0.5 rounded bg-green-100 text-green-700 font-medium">
                Always shown
              </span>
            </div>
            <div className="rounded-xl border border-zinc-200 overflow-hidden">
              <table className="w-full text-xs">
                <thead className="bg-zinc-50">
                  <tr>
                    <th className="px-3 py-2 text-left font-semibold text-zinc-500">
                      Field
                    </th>
                    <th className="px-3 py-2 text-left font-semibold text-zinc-500">
                      Label shown to agent
                    </th>
                    <th className="px-3 py-2 text-left font-semibold text-zinc-500">
                      Type
                    </th>
                    <th className="px-3 py-2 text-left font-semibold text-zinc-500">
                      Variable (copy for HTTP POST)
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-zinc-100">
                  {STANDARD_FIELDS[activeEntity].map((f) => (
                    <tr key={f.fieldName} className="bg-white">
                      <td className="px-3 py-2 font-mono text-[10px] text-zinc-600">
                        {f.fieldName}
                      </td>
                      <td className="px-3 py-2 text-zinc-700">{f.label}</td>
                      <td className="px-3 py-2">
                        <TypeBadge type={f.type} />
                      </td>
                      <td className="px-3 py-2">
                        <VariableChip
                          entity={activeEntity}
                          fieldName={f.fieldName}
                        />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Custom fields for this entity */}
          <div className="mb-5">
            <div className="flex items-center gap-2 mb-2">
              <div className="text-xs font-semibold text-zinc-500 uppercase tracking-wide">
                Custom fields
              </div>
              {entityCustomFields.length > 0 && (
                <span className="text-[10px] px-1.5 py-0.5 rounded bg-blue-100 text-blue-700 font-medium">
                  {entityCustomFields.length} added
                </span>
              )}
            </div>

            {entityCustomFields.length === 0 ? (
              <div className="rounded-xl border border-dashed border-zinc-200 p-5 text-center text-xs text-zinc-400">
                No custom {entityConfig.label.toLowerCase()} fields added yet.
                Discover your Dynamics properties below and add any you want
                agents to see.
              </div>
            ) : (
              <div className="rounded-xl border border-zinc-200 overflow-hidden">
                <table className="w-full text-xs">
                  <thead className="bg-zinc-50">
                    <tr>
                      <th className="px-3 py-2 text-left font-semibold text-zinc-500">
                        Field
                      </th>
                      <th className="px-3 py-2 text-left font-semibold text-zinc-500">
                        Label shown to agent
                      </th>
                      <th className="px-3 py-2 text-left font-semibold text-zinc-500">
                        Type
                      </th>
                      <th className="px-3 py-2 text-left font-semibold text-zinc-500">
                        Variable
                      </th>
                      <th className="px-3 py-2" />
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-zinc-100">
                    {entityCustomFields.map((f) => (
                      <tr key={f.fieldName} className="bg-white">
                        <td className="px-3 py-2 font-mono text-[10px] text-zinc-600">
                          {f.fieldName}
                        </td>
                        <td className="px-3 py-2 text-zinc-700">{f.label}</td>
                        <td className="px-3 py-2">
                          <TypeBadge type={f.attributeType || "String"} />
                        </td>
                        <td className="px-3 py-2">
                          <VariableChip
                            entity={activeEntity}
                            fieldName={f.fieldName}
                          />
                        </td>
                        <td className="px-3 py-2">
                          <button
                            type="button"
                            onClick={() =>
                              removeField(f.fieldName, activeEntity)
                            }
                            aria-label={`Remove ${f.label}`}
                            className="text-zinc-300 hover:text-red-400 transition-colors"
                          >
                            <svg
                              viewBox="0 0 12 12"
                              width="12"
                              height="12"
                              fill="none"
                            >
                              <path
                                d="M2 2L10 10M10 2L2 10"
                                stroke="currentColor"
                                strokeWidth="1.5"
                                strokeLinecap="round"
                              />
                            </svg>
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* Property discovery picker */}
          <div className="rounded-xl border border-blue-200 bg-blue-50 p-4 mb-5">
            <div className="text-xs font-semibold text-blue-800 mb-3">
              Add a custom {entityConfig.label} field
            </div>
            {!entityPropertiesLoaded ? (
              <PrimaryButton
                onClick={() => loadProperties(activeEntity)}
                disabled={loadingProperties}
              >
                {loadingProperties
                  ? "Discovering properties…"
                  : `Discover ${entityConfig.label} properties from Dynamics`}
              </PrimaryButton>
            ) : (
              <div className="space-y-2">
                <FieldInput
                  type="text"
                  placeholder="Filter properties by name…"
                  value={filter}
                  onChange={(e) => {
                    setFilter(e.target.value);
                    setSelectedProp("");
                  }}
                />
                <div className="flex gap-2">
                  <FieldInput
                    as="select"
                    value={selectedProp}
                    onChange={(e) => {
                      setSelectedProp(e.target.value);
                      const found = filteredProperties.find(
                        (p) => p.name === e.target.value
                      );
                      if (found && !customLabel) setCustomLabel(found.label);
                    }}
                    size={Math.min(filteredProperties.length + 1, 7)}
                  >
                    <option value="">— select a property —</option>
                    {filteredProperties.map((p) => (
                      <option key={p.name} value={p.name}>
                        {p.label}
                        {p.label !== p.name ? ` (${p.name})` : ""} [
                        {p.attributeType}]
                      </option>
                    ))}
                  </FieldInput>
                </div>
                <div className="flex gap-2">
                  <FieldInput
                    type="text"
                    placeholder="Friendly label for agents (optional — defaults to property display name)"
                    value={customLabel}
                    onChange={(e) => setCustomLabel(e.target.value)}
                  />
                  <PrimaryButton onClick={addField} disabled={!selectedProp}>
                    + Add field
                  </PrimaryButton>
                </div>
                <p className="text-[10px] text-blue-600">
                  {entityProperties.length} {entityConfig.label} properties
                  discovered from your Dynamics org.
                  {filter &&
                    filteredProperties.length !== entityProperties.length &&
                    ` Showing ${filteredProperties.length} matching "${filter}".`}
                </p>
              </div>
            )}
          </div>

          {/* Save */}
          <div className="flex items-center gap-3">
            <PrimaryButton onClick={save} disabled={saving}>
              {saving ? "Saving…" : "Save all sidebar fields"}
            </PrimaryButton>
            {saved && (
              <span className="text-sm text-green-600 font-medium">
                ✓ Saved — agents see the updated fields immediately.
              </span>
            )}
          </div>
        </div>
      </SectionCard>
    </div>
  );
}

// ── Field Mapping Panel ────────────────────────────────────────────────────────

const VELARO_CONTACT_FIELDS = [
  { value: "email", label: "Email" },
  { value: "phone", label: "Phone" },
  { value: "mobile", label: "Mobile" },
  { value: "firstName", label: "First Name" },
  { value: "lastName", label: "Last Name" },
  { value: "company", label: "Company" },
  { value: "companyWebsite", label: "Company Website" },
  { value: "jobTitle", label: "Job Title" },
  { value: "city", label: "City" },
  { value: "state", label: "State" },
  { value: "zipCode", label: "Zip Code" },
  { value: "country", label: "Country" },
  { value: "address", label: "Address" }
];

const STANDARD_MAPPINGS: { velaro: string; dynamics: string; label: string }[] =
  [
    { velaro: "email", dynamics: "emailaddress1", label: "Email" },
    { velaro: "phone", dynamics: "telephone1", label: "Phone" },
    { velaro: "mobile", dynamics: "mobilephone", label: "Mobile" },
    { velaro: "firstName", dynamics: "firstname", label: "First Name" },
    { velaro: "lastName", dynamics: "lastname", label: "Last Name" },
    { velaro: "company", dynamics: "companyname", label: "Company" },
    {
      velaro: "companyWebsite",
      dynamics: "websiteurl",
      label: "Company Website"
    },
    { velaro: "jobTitle", dynamics: "jobtitle", label: "Job Title" },
    { velaro: "address", dynamics: "address1_line1", label: "Address" },
    { velaro: "city", dynamics: "address1_city", label: "City" },
    {
      velaro: "state",
      dynamics: "address1_stateorprovince",
      label: "State / Province"
    },
    {
      velaro: "zipCode",
      dynamics: "address1_postalcode",
      label: "Zip / Postal Code"
    },
    { velaro: "country", dynamics: "address1_country", label: "Country" }
  ];

function DynamicsFieldMappingsPanel({
  configs,
  api,
  onSaved
}: {
  configs: DynamicsConfig[];
  api: ReturnType<typeof useApi>;
  onSaved: () => void;
}) {
  const activeConfig = configs.find((c) => c.enabled) ?? configs[0];
  const [mappings, setMappings] = React.useState<DynamicsFieldMapping[]>(() => {
    try {
      return activeConfig?.customFieldMappingsJson
        ? JSON.parse(activeConfig.customFieldMappingsJson)
        : [];
    } catch {
      return [];
    }
  });
  const [saving, setSaving] = React.useState(false);
  const { displayToast } = useToastAlert();

  // Root cause (2026-09-07 QA — "Custom mappings save the state from before the edit"): every
  // row's onBlur closed directly over the `mappings` render variable, so it saved whatever array
  // was captured when THAT row's JSX was created rather than the row's own just-typed edit — a
  // second, unrelated blur/click firing an older row's stale onBlur could overwrite a real edit
  // with a probe value that was never meant to be saved. A ref updated synchronously at every
  // single mutation site (never via a useEffect watching `mappings`, which has the same one-render
  // lag) is the fix — onBlur reads the ref, never the closure.
  const mappingsRef = React.useRef(mappings);

  async function saveMappings(next: DynamicsFieldMapping[]) {
    if (!activeConfig) return;
    setSaving(true);
    try {
      const resp = await api.admin.put(`Dynamics/Configs/${activeConfig.id}`, {
        customFieldMappingsJson: JSON.stringify(next)
      });
      if (resp.ok) {
        setMappings(next);
        mappingsRef.current = next;
        onSaved();
        displayToast(Severity.Success, "Field mappings saved.");
      } else displayToast(Severity.Error, "Failed to save field mappings.");
    } catch (err) {
      displayToast(Severity.Error, `Save failed: ${String(err)}`);
    } finally {
      setSaving(false);
    }
  }

  function addRow() {
    const next = [
      ...mappings,
      { velaroAttribute: "", dynamicsField: "", label: "" }
    ];
    mappingsRef.current = next;
    setMappings(next);
  }
  function removeRow(i: number) {
    const next = mappings.filter((_, idx) => idx !== i);
    mappingsRef.current = next;
    setMappings(next);
    saveMappings(next);
  }
  function updateRow(
    i: number,
    field: keyof DynamicsFieldMapping,
    value: string
  ) {
    const next = mappings.map((m, idx) =>
      idx === i ? { ...m, [field]: value } : m
    );
    mappingsRef.current = next;
    setMappings(next);
  }

  return (
    <SectionCard title="Field Mapping">
      <p className="text-sm text-zinc-500 mb-4">
        Standard Velaro contact fields are mapped to Dynamics 365 automatically.
        Add custom mappings below to push additional contact attributes into any
        Dynamics field — including custom fields like{" "}
        <code className="text-xs bg-zinc-100 px-1 rounded">
          cr123_company_size
        </code>
        .
      </p>

      {/* Standard mappings — read-only display */}
      <div className="mb-5">
        <div className="text-xs font-semibold text-zinc-500 uppercase tracking-wide mb-2">
          Standard (automatic)
        </div>
        <div className="border border-zinc-100 rounded-lg overflow-hidden">
          <table className="w-full text-xs">
            <thead className="bg-zinc-50 border-b border-zinc-100">
              <tr>
                <th className="px-3 py-2 text-left text-zinc-500 font-medium">
                  Velaro Field
                </th>
                <th className="px-3 py-2 text-left text-zinc-500 font-medium">
                  Dynamics 365 Field
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-zinc-50">
              {STANDARD_MAPPINGS.map((m) => (
                <tr key={m.velaro} className="text-zinc-600">
                  <td className="px-3 py-1.5 font-medium">{m.label}</td>
                  <td className="px-3 py-1.5 font-mono text-zinc-400">
                    {m.dynamics}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Custom mappings — editable */}
      <div>
        <div className="text-xs font-semibold text-zinc-500 uppercase tracking-wide mb-2">
          Custom attribute mappings
        </div>
        <p className="text-xs text-zinc-400 mb-3">
          Map any Velaro contact attribute (collected via your bot workflow) to
          a Dynamics custom field. The bot collects these during conversation —
          use the exact attribute name from your workflow.
        </p>
        {mappings.length > 0 && (
          <div className="space-y-2 mb-3">
            {mappings.map((m, i) => (
              <div key={i} className="flex items-center gap-2">
                <input
                  value={m.velaroAttribute}
                  onChange={(e) =>
                    updateRow(i, "velaroAttribute", e.target.value)
                  }
                  onBlur={() => saveMappings(mappingsRef.current)}
                  placeholder="Velaro attribute name (e.g. company_size)"
                  className="flex-1 text-xs px-2 py-1.5 border border-zinc-200 rounded-md focus:outline-none focus:ring-1 focus:ring-primary/30"
                />
                <span className="text-zinc-400 text-xs">→</span>
                <input
                  value={m.dynamicsField}
                  onChange={(e) =>
                    updateRow(i, "dynamicsField", e.target.value)
                  }
                  onBlur={() => saveMappings(mappingsRef.current)}
                  placeholder="Dynamics field name (e.g. cr123_company_size)"
                  className="flex-1 text-xs px-2 py-1.5 border border-zinc-200 rounded-md font-mono focus:outline-none focus:ring-1 focus:ring-primary/30"
                />
                <input
                  value={m.label}
                  onChange={(e) => updateRow(i, "label", e.target.value)}
                  onBlur={() => saveMappings(mappingsRef.current)}
                  placeholder="Label (optional)"
                  className="w-32 text-xs px-2 py-1.5 border border-zinc-200 rounded-md focus:outline-none focus:ring-1 focus:ring-primary/30"
                />
                <IconButton
                  type="button"
                  onClick={() => removeRow(i)}
                  aria-label="Remove mapping"
                >
                  <Trash className="w-4 h-4" />
                </IconButton>
              </div>
            ))}
          </div>
        )}
        <button
          type="button"
          onClick={addRow}
          className="text-xs text-primary hover:text-cornflower-blue-700 border border-dashed border-cornflower-blue-200 rounded-md px-3 py-1.5 transition-colors"
        >
          + Add custom mapping
        </button>
        {saving && <span className="ml-3 text-xs text-zinc-400">Saving…</span>}
      </div>
    </SectionCard>
  );
}

export default function Dynamics() {
  const api = useApi();
  const navigate = useNavigate();
  const [configs, setConfigs] = useState<DynamicsConfig[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<
    "overview" | "config" | "skills" | "mapping" | "log"
  >("overview");
  const [showWizard, setShowWizard] = useState(false);

  const fetchConfigs = useCallback(async () => {
    try {
      const resp = await api.admin.get("Dynamics/Configs");
      const data = await resp.json();
      setConfigs(Array.isArray(data) ? data : []);
    } catch {
      setConfigs([]);
    } finally {
      setLoading(false);
    }
  }, [api.admin]);

  useEffect(() => {
    fetchConfigs();
  }, [fetchConfigs]);

  const hasActiveConfig = configs.some((c) => c.enabled);
  const expiredConfigs = configs.filter((c) => c.secretStatus === "expired");
  const expiringSoonConfigs = configs.filter(
    (c) => c.secretStatus === "expiring_soon"
  );

  async function togglePushTranscripts(config: DynamicsConfig) {
    try {
      const resp = await api.admin.put(`Dynamics/Configs/${config.id}`, {
        pushTranscripts: !config.pushTranscripts
      });
      if (!resp.ok) throw resp;
      const updated = await resp.json();
      setConfigs((prev) =>
        prev.map((c) =>
          c.id === config.id
            ? { ...c, pushTranscripts: updated.pushTranscripts }
            : c
        )
      );
    } catch {
      // ignore
    }
  }

  async function toggleAttachAsActivity(config: DynamicsConfig) {
    try {
      const resp = await api.admin.put(`Dynamics/Configs/${config.id}`, {
        attachAsActivity: !config.attachAsActivity
      });
      if (!resp.ok) throw resp;
      const updated = await resp.json();
      setConfigs((prev) =>
        prev.map((c) =>
          c.id === config.id
            ? { ...c, attachAsActivity: updated.attachAsActivity }
            : c
        )
      );
    } catch {
      // ignore
    }
  }

  async function togglePreferAccount(config: DynamicsConfig) {
    try {
      const resp = await api.admin.put(`Dynamics/Configs/${config.id}`, {
        preferAccount: !config.preferAccount
      });
      if (!resp.ok) throw resp;
      const updated = await resp.json();
      setConfigs((prev) =>
        prev.map((c) =>
          c.id === config.id
            ? { ...c, preferAccount: updated.preferAccount }
            : c
        )
      );
    } catch {
      // ignore
    }
  }

  async function updateActivityLogicalName(
    config: DynamicsConfig,
    value: string
  ) {
    try {
      // FIXED (QA 2026-09-04): sending `null` here to mean "cleared" never actually cleared the
      // field -- the backend (DynamicsController.UpdateConfig) treats `null` as "field not
      // provided, leave unchanged" (`if (request?.ActivityLogicalName != null) ...`), which is the
      // correct behavior for a PATCH-style partial update but meant clearing was a no-op forever.
      // Send an empty string instead, which the backend DOES apply (empty string != null).
      const resp = await api.admin.put(`Dynamics/Configs/${config.id}`, {
        activityLogicalName: value.trim()
      });
      if (!resp.ok) throw resp;
      const updated = await resp.json();
      setConfigs((prev) =>
        prev.map((c) =>
          c.id === config.id
            ? { ...c, activityLogicalName: updated.activityLogicalName }
            : c
        )
      );
    } catch {
      // ignore
    }
  }

  const tabsJSX = (
    <>
      <IntegrationTab
        active={activeTab === "overview"}
        onClick={() => setActiveTab("overview")}
      >
        Overview
      </IntegrationTab>
      <IntegrationTab
        active={activeTab === "config"}
        onClick={() => setActiveTab("config")}
      >
        Configuration
      </IntegrationTab>
      <IntegrationTab
        active={activeTab === "skills"}
        onClick={() => setActiveTab("skills")}
      >
        AI Skills
      </IntegrationTab>
      <IntegrationTab
        active={activeTab === "mapping"}
        onClick={() => setActiveTab("mapping")}
      >
        Data Mapping
      </IntegrationTab>
      <IntegrationTab
        active={activeTab === "log"}
        onClick={() => setActiveTab("log")}
      >
        Activity Log
      </IntegrationTab>
    </>
  );

  return (
    <StickyRouteHeader title="Dynamics 365" tabs={tabsJSX}>
      {/* Credential expiry banners — shown regardless of active tab */}
      {expiredConfigs.length > 0 && (
        <div className="mb-4 bg-red-50 border border-red-200 rounded-xl p-4 flex items-start gap-3">
          <svg
            className="w-5 h-5 text-red-600 shrink-0 mt-0.5"
            fill="currentColor"
            viewBox="0 0 20 20"
          >
            <path
              fillRule="evenodd"
              d="M8.485 2.495c.673-1.167 2.357-1.167 3.03 0l6.28 10.875c.673 1.167-.17 2.625-1.516 2.625H3.72c-1.347 0-2.189-1.458-1.515-2.625L8.485 2.495zM10 5a.75.75 0 01.75.75v3.5a.75.75 0 01-1.5 0v-3.5A.75.75 0 0110 5zm0 9a1 1 0 100-2 1 1 0 000 2z"
              clipRule="evenodd"
            />
          </svg>
          <div>
            <div className="text-sm font-semibold text-red-800">
              {expiredConfigs.length === 1
                ? `"${expiredConfigs[0].displayName || expiredConfigs[0].orgName}" client secret has expired`
                : `${expiredConfigs.length} Dynamics 365 client secrets have expired`}
            </div>
            <div className="text-xs text-red-700 mt-0.5">
              AI tools and workflows using{" "}
              {expiredConfigs.length === 1
                ? "this connection"
                : "these connections"}{" "}
              are failing. Renew the secret in Azure Portal → App Registrations
              → Certificates &amp; secrets, then re-enter it here.
            </div>
          </div>
        </div>
      )}
      {expiringSoonConfigs.length > 0 && expiredConfigs.length === 0 && (
        <div className="mb-4 bg-amber-50 border border-amber-200 rounded-xl p-4 flex items-start gap-3">
          <svg
            className="w-5 h-5 text-amber-600 shrink-0 mt-0.5"
            fill="currentColor"
            viewBox="0 0 20 20"
          >
            <path
              fillRule="evenodd"
              d="M8.485 2.495c.673-1.167 2.357-1.167 3.03 0l6.28 10.875c.673 1.167-.17 2.625-1.516 2.625H3.72c-1.347 0-2.189-1.458-1.515-2.625L8.485 2.495zM10 5a.75.75 0 01.75.75v3.5a.75.75 0 01-1.5 0v-3.5A.75.75 0 0110 5zm0 9a1 1 0 100-2 1 1 0 000 2z"
              clipRule="evenodd"
            />
          </svg>
          <div>
            <div className="text-sm font-semibold text-amber-800">
              {expiringSoonConfigs.length === 1
                ? `"${expiringSoonConfigs[0].displayName || expiringSoonConfigs[0].orgName}" client secret expires soon`
                : `${expiringSoonConfigs.length} Dynamics 365 client secrets expiring soon`}
            </div>
            <div className="text-xs text-amber-700 mt-0.5">
              Renew before expiry to avoid an outage. Go to Azure Portal → App
              Registrations → Certificates &amp; secrets.
            </div>
          </div>
        </div>
      )}

      {showWizard && (
        <StarterWorkflowWizard
          integration="dynamics"
          dynamicsConfigId={configs.find((c) => c.enabled)?.id}
          onClose={() => setShowWizard(false)}
        />
      )}

      {activeTab === "overview" && <CapabilitiesPanel />}

      {activeTab === "config" && (
        <div>
          {hasActiveConfig && (
            <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 mb-4 flex flex-col gap-3">
              <div className="flex items-center justify-between gap-4">
                <div>
                  <div className="text-sm font-medium text-blue-800 mb-0.5">
                    Deploy the chat widget to your site
                  </div>
                  <div className="text-xs text-blue-600">
                    Your Dynamics 365 integration is connected — grab your embed
                    code from Deployments to go live.
                  </div>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <Link
                    to="/chat_window"
                    className="text-xs px-3 py-1.5 rounded-md text-blue-700 hover:bg-blue-100 transition"
                  >
                    Chat window
                  </Link>
                  <Link
                    to="/deployment"
                    className="text-xs px-3 py-1.5 rounded-lg bg-(--ds-primary) hover:opacity-90 text-white transition"
                  >
                    Get embed code
                  </Link>
                </div>
              </div>
              <div className="flex items-start gap-2 bg-white/60 rounded-lg px-3 py-2 text-xs text-blue-700 border border-blue-100">
                <Lightbulb className="w-4 h-4 mt-0.5 shrink-0" />
                <span>
                  <strong>Tip:</strong> You can also create a{" "}
                  <strong>Knowledge Base Q&A</strong> page widget from{" "}
                  <Link
                    to="/deployment"
                    className="underline hover:text-blue-900"
                  >
                    Deployments → Page Widgets
                  </Link>{" "}
                  for a lightweight, AI-only Q&A experience — no live agent
                  needed.
                </span>
              </div>
            </div>
          )}

          {loading ? (
            <div className="flex justify-center py-12">
              <LoadingSpinner />
            </div>
          ) : (
            <>
              <div className="mb-6">
                <DynamicsAccounts
                  configs={configs}
                  onDelete={(id) =>
                    setConfigs((prev) => prev.filter((c) => c.id !== id))
                  }
                  onRefresh={fetchConfigs}
                />
              </div>

              {/* Transcript auto-push */}
              {configs.length > 0 && (
                <SectionCard title="Transcript Auto-Push" className="mb-4">
                  <div className="text-sm text-zinc-500 mb-4">
                    When enabled, the full chat transcript is automatically
                    attached as a note on the matched Dynamics 365 contact or
                    lead after every conversation ends.
                  </div>
                  <div className="space-y-3">
                    {configs.map((config) => (
                      <div key={config.id}>
                        <div className="flex items-center justify-between gap-4 p-3 border-b border-zinc-100 last:border-b-0">
                          <div>
                            <div className="text-sm font-medium text-zinc-700">
                              {config.displayName ||
                                config.orgName ||
                                config.instanceUrl}
                            </div>
                            <div className="text-xs text-zinc-500 mt-0.5">
                              {config.pushTranscripts
                                ? "Transcript attached to contact/lead after each conversation"
                                : "Transcript push is off for this connection"}
                            </div>
                          </div>
                          <button
                            type="button"
                            onClick={() => togglePushTranscripts(config)}
                            className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors focus:outline-none shrink-0 ${config.pushTranscripts ? "bg-green-500" : "bg-zinc-300"}`}
                            aria-label={
                              config.pushTranscripts
                                ? "Disable transcript push"
                                : "Enable transcript push"
                            }
                          >
                            <span
                              className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white shadow transition-transform ${config.pushTranscripts ? "translate-x-4" : "translate-x-0.5"}`}
                            />
                          </button>
                        </div>

                        {config.pushTranscripts && (
                          <div className="pl-3 pb-3 -mt-1 space-y-2 border-b border-zinc-100 last:border-b-0">
                            <div className="flex items-center justify-between gap-4">
                              <div>
                                <div className="text-xs font-medium text-zinc-600">
                                  Attach as Activity instead of Note
                                </div>
                                <div className="text-[11px] text-zinc-400 mt-0.5">
                                  Shows in the Activities tab, but long
                                  transcripts are truncated — a Note carries the
                                  full transcript as an attached file, an
                                  Activity does not.
                                </div>
                              </div>
                              <button
                                type="button"
                                onClick={() => toggleAttachAsActivity(config)}
                                className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors focus:outline-none shrink-0 ${config.attachAsActivity ? "bg-green-500" : "bg-zinc-300"}`}
                                aria-label={
                                  config.attachAsActivity
                                    ? "Attach as Note instead"
                                    : "Attach as Activity instead"
                                }
                              >
                                <span
                                  className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white shadow transition-transform ${config.attachAsActivity ? "translate-x-4" : "translate-x-0.5"}`}
                                />
                              </button>
                            </div>

                            {config.attachAsActivity && (
                              <>
                                <div className="flex items-center justify-between gap-4">
                                  <div>
                                    <div className="text-xs font-medium text-zinc-600">
                                      Prefer parent Account when available
                                    </div>
                                    <div className="text-[11px] text-zinc-400 mt-0.5">
                                      Targets the linked record&rsquo;s parent
                                      Account when one resolves (e.g. a
                                      Contact&rsquo;s company); falls back to
                                      the linked record itself — never to a Note
                                      — when no Account is found.
                                    </div>
                                  </div>
                                  <button
                                    type="button"
                                    onClick={() => togglePreferAccount(config)}
                                    className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors focus:outline-none shrink-0 ${config.preferAccount ? "bg-green-500" : "bg-zinc-300"}`}
                                    aria-label={
                                      config.preferAccount
                                        ? "Stop preferring parent Account"
                                        : "Prefer parent Account"
                                    }
                                  >
                                    <span
                                      className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white shadow transition-transform ${config.preferAccount ? "translate-x-4" : "translate-x-0.5"}`}
                                    />
                                  </button>
                                </div>

                                <div>
                                  <label className="text-xs font-medium text-zinc-600 block mb-1">
                                    Activity type
                                  </label>
                                  <input
                                    type="text"
                                    defaultValue={
                                      config.activityLogicalName ?? ""
                                    }
                                    placeholder="phonecall"
                                    onBlur={(e) => {
                                      if (
                                        e.target.value.trim() !==
                                        (config.activityLogicalName ?? "")
                                      ) {
                                        updateActivityLogicalName(
                                          config,
                                          e.target.value
                                        );
                                      }
                                    }}
                                    className="w-full max-w-xs text-xs border border-zinc-200 rounded px-2 py-1 focus:outline-none focus:ring-1 focus:ring-cornflower-blue-300 focus:border-cornflower-blue-300"
                                  />
                                  <div className="text-[11px] text-zinc-400 mt-0.5">
                                    The logical name of the Dynamics activity
                                    entity to create, e.g. phonecall or task.
                                    Leave blank to use phonecall.
                                  </div>
                                </div>
                              </>
                            )}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                  {configs.some((c) => c.pushTranscripts && c.enabled) && (
                    <div className="mt-3 bg-green-50 border border-green-200 rounded-lg p-3 text-xs text-green-700">
                      Auto-push active — transcripts will appear as{" "}
                      {configs.some((c) => c.attachAsActivity)
                        ? "notes or activities"
                        : "notes"}{" "}
                      on the matched Dynamics 365 contact, lead, or account
                      after each conversation.
                    </div>
                  )}
                </SectionCard>
              )}

              {/* Rules engine info */}
              {hasActiveConfig && (
                <SectionCard title="Rules Engine Integration">
                  <div className="font-semibold text-zinc-800 text-lg mb-2 flex items-center gap-2">
                    <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-blue-100 text-blue-700">
                      Active
                    </span>
                  </div>
                  <div className="text-sm text-zinc-500 mb-3">
                    Use the Rules Engine to automatically create Dynamics 365
                    records when conversations match conditions — no agent
                    action required.
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-4">
                    {[
                      "Contact",
                      "Lead",
                      "Account",
                      "Opportunity",
                      "Case",
                      "Note"
                    ].map((r) => (
                      <div
                        key={r}
                        className="flex items-center gap-2 p-2 rounded-lg border border-blue-100 bg-blue-50 text-sm text-blue-700"
                      >
                        <svg
                          viewBox="0 0 10 10"
                          width="8"
                          height="8"
                          fill="none"
                        >
                          <path
                            d="M1.5 5L3.5 7L8.5 3"
                            stroke="#3B82F6"
                            strokeWidth="1.5"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                        {r}
                      </div>
                    ))}
                  </div>
                  <button
                    type="button"
                    onClick={() => navigate("/rules")}
                    className="text-sm px-4 py-2 bg-blue-50 border border-blue-300 text-blue-700 rounded-lg hover:bg-blue-100"
                  >
                    Go to Rules Engine →
                  </button>
                </SectionCard>
              )}
            </>
          )}
        </div>
      )}

      {activeTab === "skills" && (
        <>
          {loading ? (
            <div className="flex justify-center py-12">
              <LoadingSpinner />
            </div>
          ) : configs.length > 0 ? (
            <SectionCard className="mb-4">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <div className="font-semibold text-zinc-800 text-lg flex items-center gap-2">
                    AI Tools
                    <span
                      className={`text-xs font-medium px-2 py-0.5 rounded-full ${hasActiveConfig ? "bg-green-100 text-green-700" : "bg-zinc-100 text-zinc-500"}`}
                    >
                      {hasActiveConfig ? "Active" : "No active connection"}
                    </span>
                  </div>
                  <div className="text-sm text-zinc-500 mt-0.5">
                    28 tools available to any AI workflow node on this site. No
                    configuration required — the AI decides when to call
                    Dynamics 365 based on conversation context.
                  </div>
                </div>
                {hasActiveConfig && (
                  <LaunchWizardButton onClick={() => setShowWizard(true)} />
                )}
              </div>

              <Divider />
              <div className="pt-4">
                <div className="flex items-center gap-2 mb-3">
                  <div className="text-xs font-semibold text-zinc-400 uppercase tracking-wide">
                    Available AI tools
                  </div>
                  <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-blue-100 text-blue-700">
                    28 tools
                  </span>
                </div>
                {AI_TOOL_GROUPS.map((group) => (
                  <div key={group} className="mb-4">
                    <div className="text-[10px] font-semibold text-zinc-400 uppercase tracking-wide mb-2">
                      {group}
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                      {AI_TOOLS.filter((t) => t.group === group).map((tool) => (
                        <div
                          key={tool.name}
                          className={`flex gap-3 p-3 rounded-lg border ${hasActiveConfig ? "border-blue-200 bg-blue-50" : "border-zinc-200 bg-zinc-50"}`}
                        >
                          <div
                            className={`mt-0.5 w-4 h-4 rounded-full shrink-0 flex items-center justify-center ${hasActiveConfig ? "bg-blue-500" : "bg-zinc-300"}`}
                          >
                            {hasActiveConfig && (
                              <svg
                                viewBox="0 0 10 10"
                                width="8"
                                height="8"
                                fill="none"
                              >
                                <path
                                  d="M1.5 5L3.5 7L8.5 3"
                                  stroke="white"
                                  strokeWidth="1.5"
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                />
                              </svg>
                            )}
                          </div>
                          <div>
                            <div className="text-sm font-medium text-zinc-700">
                              {tool.label}
                            </div>
                            <div className="text-xs text-zinc-500 mt-0.5">
                              {tool.description}
                            </div>
                            <code className="text-[10px] text-zinc-400 mt-1 block">
                              {tool.name}
                            </code>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>

              <div className="mt-8">
                <Divider />
                <div className="pt-8">
                  <SkillControlsPanel
                    integration="dynamics"
                    skills={AI_TOOLS}
                  />
                </div>
              </div>
            </SectionCard>
          ) : null}
        </>
      )}

      {activeTab === "mapping" && (
        <>
          {loading ? (
            <div className="flex justify-center py-12">
              <LoadingSpinner />
            </div>
          ) : (
            <>
              <DynamicsSidebarFieldsTab />
              {hasActiveConfig && (
                <DynamicsFieldMappingsPanel
                  configs={configs}
                  api={api}
                  onSaved={fetchConfigs}
                />
              )}
            </>
          )}
        </>
      )}

      {activeTab === "log" && (
        <SectionCard title="Activity Log">
          <IntegrationActivityLog integrationSlug="Dynamics365" />
        </SectionCard>
      )}
    </StickyRouteHeader>
  );
}

export function BackToIntegrationLandingDynamics() {
  const navigate = useNavigate();
  return (
    <div
      className="flex cursor-pointer"
      onClick={() => navigate("/integrations")}
    >
      <IconClose1 className="h-6 w-6" />
    </div>
  );
}
